import { getHighestProductLevelFromProductInventory, getProductsFromProductInventoryQuantityPackQuantity, getProductVersionFromProductInventory } from 'pricing-pages-lib/utils/productInventoryUtils';
import { getSku } from 'pricing-pages-lib/utils/productsUtils';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { isMarketingContactsSkuV2 } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { EMAIL_SENDS } from '../constants/LimitIds';
import { isQuantityPackDetails } from './typeguards';
export const getContactsQuantityMultiplier = (state, {
  quantityPackSkuId
}) => {
  var _sku$includedWithQuan;
  const sku = getSku(state, {
    skuId: quantityPackSkuId
  });
  if (isQuantityPackDetails(sku)) return (_sku$includedWithQuan = sku.includedWithQuantityPack) !== null && _sku$includedWithQuan !== void 0 ? _sku$includedWithQuan : 0;
  return 0;
};
export const getEmailSendsLimitMultiplier = (state, {
  quantityPackSkuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const sku = skuMap[quantityPackSkuId];
  if (!isQuantityPackDetails(sku)) return 0;
  const {
    includedWithQuantityPack,
    packagedQuantities
  } = sku;
  const emailSendLimit = packagedQuantities.find(limit => limit.name === EMAIL_SENDS);
  if (!emailSendLimit || !includedWithQuantityPack) return 0;
  return emailSendLimit.quantity / includedWithQuantityPack;
};
export const getMinimumNumberOfPurchasableContacts = ({
  includedAdditionalUnits,
  numContactsInCrm,
  marketingContactsOnRenewal,
  contactsQuantityMultiplier,
  purchasedContactsQuantity = 0,
  isMarketingContactsSku,
  totalOwnedMarketingContactsInCurrentPlan = 0
}) => {
  // missing information in state
  if (numContactsInCrm === null || isMarketingContactsSku && marketingContactsOnRenewal === null || !contactsQuantityMultiplier) {
    return null;
  }
  if (isMarketingContactsSku) {
    const billableAdditionalContacts = Math.max(0, marketingContactsOnRenewal - includedAdditionalUnits);
    let additionalContactsForSelectedPlanToMatchCurrentPlan = 0;
    while (includedAdditionalUnits + additionalContactsForSelectedPlanToMatchCurrentPlan < totalOwnedMarketingContactsInCurrentPlan) {
      additionalContactsForSelectedPlanToMatchCurrentPlan += contactsQuantityMultiplier;
    }
    return Math.ceil(Math.max(additionalContactsForSelectedPlanToMatchCurrentPlan, billableAdditionalContacts) / contactsQuantityMultiplier) * contactsQuantityMultiplier;
  } else {
    // hasn't bought contacts before
    if (!purchasedContactsQuantity) {
      // contacts in crm is fewer than minimum contacts purchase quantity
      if (numContactsInCrm < includedAdditionalUnits) {
        return includedAdditionalUnits;
      }
      // contacts in crm is fewer than quantity multiplier
      if (numContactsInCrm <= contactsQuantityMultiplier) {
        return contactsQuantityMultiplier;
      }
    }
    const purchasableContactsFromCrm = Math.ceil(numContactsInCrm / contactsQuantityMultiplier) * contactsQuantityMultiplier;
    return Math.max(purchasableContactsFromCrm, purchasedContactsQuantity);
  }
};
const _getPurchasedContactsQuantity = (state, {
  filter
}) => {
  const productLevel = getHighestProductLevelFromProductInventory(state, {
    productLine: MARKETING
  });
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters: {
      productLevel,
      productLine: MARKETING
    }
  });
  if (!productVersion) return 0;
  if (filter && !filter(productVersion)) return 0;
  return getProductsFromProductInventoryQuantityPackQuantity(state, {
    productIdentifier: {
      productLevel,
      productLine: MARKETING
    },
    quantityPackApiName: 'additional-marketing-contacts'
  });
};
export const getPurchasedMarketingContactsQuantity = state => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return _getPurchasedContactsQuantity(state, {
    filter: ({
      skuId
    }) => isMarketingContactsSkuV2({
      skuMap,
      skuId
    })
  });
};
export const getPurchasedNonMarketingContactsQuantity = state => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return _getPurchasedContactsQuantity(state, {
    filter: ({
      skuId
    }) => !isMarketingContactsSkuV2({
      skuMap,
      skuId
    })
  });
};

/**
 * @description
 * If using the result of this function in % (modulo) operation, handle `Infinity`.
 * `Infinity % x` will result in `NaN`.
 */
export const getMaxAdditionalUnits = quantityPack => {
  var _quantityPack$include;
  const maxTouchlessPurchasableQuantity = typeof quantityPack.maxTouchlessPurchasableQuantity !== 'number' ? Infinity : quantityPack.maxTouchlessPurchasableQuantity;
  const includedWithQuantityPack = (_quantityPack$include = quantityPack.includedWithQuantityPack) !== null && _quantityPack$include !== void 0 ? _quantityPack$include : 0;
  return includedWithQuantityPack * maxTouchlessPurchasableQuantity;
};

/*
  Total units = (tier included units) + (additional units)
  We want total units to stay the same, so we need to account for
  the difference between included units when switching plans
 */
export const calculateAdditionalUnitsForTierSwitch = (oldUnits, oldQuantityPack, newQuantityPack) => {
  var _oldQuantityPack$incl, _newQuantityPack$incl, _newQuantityPack$incl2;
  if (oldQuantityPack.skuId === newQuantityPack.skuId) return oldUnits;
  const packagedQuantitiesDifference = ((_oldQuantityPack$incl = oldQuantityPack.includedWithProductVersion) !== null && _oldQuantityPack$incl !== void 0 ? _oldQuantityPack$incl : 0) - ((_newQuantityPack$incl = newQuantityPack.includedWithProductVersion) !== null && _newQuantityPack$incl !== void 0 ? _newQuantityPack$incl : 0);
  const newMarketingUnits = oldUnits + packagedQuantitiesDifference;
  const newMaxAdditionalUnits = getMaxAdditionalUnits(newQuantityPack);
  const maxMarketingUnits = newMaxAdditionalUnits - ((_newQuantityPack$incl2 = newQuantityPack.includedWithProductVersion) !== null && _newQuantityPack$incl2 !== void 0 ? _newQuantityPack$incl2 : 0);
  return Math.max(0, Math.min(maxMarketingUnits, newMarketingUnits));
};
const createSteppingFunction = (min, max, step) => (input, roundUp = false) => {
  return Math.min(Math[roundUp ? 'ceil' : 'round']((Math.max(input, min) - min) / step) * step + min, max === Infinity ? Infinity : max - (max - min) % step);
};
export const getQuantityPackSteppingFunction = quantityPack => {
  const maxAdditionalUnits = getMaxAdditionalUnits(quantityPack);
  const {
    includedWithQuantityPack
  } = quantityPack;
  return createSteppingFunction(0, maxAdditionalUnits === Infinity ? Infinity : maxAdditionalUnits - maxAdditionalUnits % ( // avoid division by zero
  includedWithQuantityPack !== null && includedWithQuantityPack !== void 0 ? includedWithQuantityPack : 1), includedWithQuantityPack !== null && includedWithQuantityPack !== void 0 ? includedWithQuantityPack : 0);
};